/**
 * 各 variant の Editor 内で使う共通エディタ部品。
 *
 * IME (日本語入力) 対策:
 *   - 入力値は内部 local state で保持し、composition 中は親に通知しない。
 *   - composition 終了時 (Enter で確定など) に親へ最新値を反映。
 *   - 親の state 変化で input が controlled される際、composition 中の
 *     value prop 上書きは IME を妨害しカーソルが末尾に飛ぶ原因になるため、
 *     value prop の同期は composition 中はスキップする。
 *   - onCommit (blur 保存) も composition 中は走らせない。
 */
import { Plus, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input, Label, Textarea } from '@/components/ui/input';
import { DocumentImageField } from '../components/DocumentImageField';

export function TextField({
  label,
  value,
  onChange,
  onCommit,
  placeholder,
  type = 'text',
  className,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  onCommit: () => void;
  placeholder?: string;
  type?: string;
  className?: string;
}) {
  const [local, setLocal] = useState(value);
  const composingRef = useRef(false);

  // 外部の value 更新を取り込む（IME 中は遅延）
  useEffect(() => {
    if (composingRef.current) return;
    setLocal(value);
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setLocal(v);
    if (!composingRef.current) {
      onChange(v);
    }
  };

  const handleCompositionEnd = (
    e: React.CompositionEvent<HTMLInputElement>,
  ) => {
    composingRef.current = false;
    const v = (e.target as HTMLInputElement).value;
    setLocal(v);
    onChange(v);
  };

  const handleBlur = () => {
    if (composingRef.current) return;
    if (local !== value) onChange(local);
    onCommit();
  };

  return (
    <div className="space-y-1">
      <Label className="text-[11px]">{label}</Label>
      <Input
        type={type}
        value={local}
        onChange={handleChange}
        onCompositionStart={() => (composingRef.current = true)}
        onCompositionEnd={handleCompositionEnd}
        onBlur={handleBlur}
        placeholder={placeholder}
        className={className}
      />
    </div>
  );
}

export function TextareaField({
  label,
  value,
  onChange,
  onCommit,
  rows = 4,
  placeholder,
  mono,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  onCommit: () => void;
  rows?: number;
  placeholder?: string;
  mono?: boolean;
}) {
  const [local, setLocal] = useState(value);
  const composingRef = useRef(false);

  useEffect(() => {
    if (composingRef.current) return;
    setLocal(value);
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const v = e.target.value;
    setLocal(v);
    if (!composingRef.current) {
      onChange(v);
    }
  };

  const handleCompositionEnd = (
    e: React.CompositionEvent<HTMLTextAreaElement>,
  ) => {
    composingRef.current = false;
    const v = (e.target as HTMLTextAreaElement).value;
    setLocal(v);
    onChange(v);
  };

  const handleBlur = () => {
    if (composingRef.current) return;
    if (local !== value) onChange(local);
    onCommit();
  };

  return (
    <div className="space-y-1">
      <Label className="text-[11px]">{label}</Label>
      <Textarea
        value={local}
        onChange={handleChange}
        onCompositionStart={() => (composingRef.current = true)}
        onCompositionEnd={handleCompositionEnd}
        onBlur={handleBlur}
        rows={rows}
        placeholder={placeholder}
        className={mono ? 'font-mono text-xs' : undefined}
      />
    </div>
  );
}

export function ImageField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <DocumentImageField
      label={label}
      value={value || null}
      onChange={(v) => onChange(v ?? '')}
    />
  );
}

export function SelectField<T extends string>({
  label,
  value,
  onChange,
  onCommit,
  options,
}: {
  label: string;
  value: T;
  onChange: (v: T) => void;
  onCommit: () => void;
  options: Array<{ value: T; label: string }>;
}) {
  return (
    <div className="space-y-1">
      <Label className="text-[11px]">{label}</Label>
      <select
        value={value}
        onChange={(e) => {
          onChange(e.target.value as T);
          onCommit();
        }}
        className="w-full px-3 py-2 text-sm bg-[var(--bg-input)] border border-[var(--border)] rounded-sm"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

export function ArrayField<T>({
  label,
  items,
  onChange,
  renderItem,
  addLabel,
  emptyItem,
}: {
  label: string;
  items: T[];
  onChange: (next: T[]) => void;
  renderItem: (item: T, index: number, update: (patch: Partial<T>) => void) => React.ReactNode;
  addLabel?: string;
  emptyItem: () => T;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-[11px]">{label}</Label>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onChange([...items, emptyItem()])}
        >
          <Plus className="size-3 mr-1" />
          {addLabel ?? '追加'}
        </Button>
      </div>
      <div className="space-y-2">
        {items.map((item, i) => (
          <div
            key={i}
            className="border border-[var(--border)] rounded-md p-2 bg-[var(--bg-elevated)] space-y-2"
          >
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-[var(--text-muted)]">
                #{i + 1}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onChange(items.filter((_, idx) => idx !== i))}
                className="text-[var(--text-muted)] hover:text-red-500"
                title="この項目を削除"
              >
                <Trash2 className="size-3" />
              </Button>
            </div>
            {renderItem(item, i, (patch) => {
              const next = items.slice();
              next[i] = { ...item, ...patch };
              onChange(next);
            })}
          </div>
        ))}
        {items.length === 0 && (
          <div className="text-[11px] text-[var(--text-muted)] p-2 text-center border border-dashed border-[var(--border)] rounded-md">
            まだ項目がありません
          </div>
        )}
      </div>
    </div>
  );
}
